/*
    Event-related code
*/

import React, {useState} from 'react';
import Loader from '../loader';
import {dateMonthShort, dateDayZeroPadded, parseDate} from '../util';
import {HighlightedText} from '../highlightedtext';
import HoverImageTile from '../hoverimagetile';

const {
    getWPJSON,
    hooks: {useBreakpoint, usePromise, useSelector,
            useA11yButton, useHover},
    components: {
        generic: {Image}
    },
    css: {prefixCSS},
    util: {times}
} = polyphony;

const styles = {
    navButton: {
        content: {
            cursor: 'pointer',
            width: '100%',
            height: '100%',
            position: 'relative'
        },
        image: prefixCSS({
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            background: '#EEE',
            opacity: 0.1
        }),
        children: prefixCSS({
            color: 'gray',
            height: '100%',
            width: '100%',
            position: 'absolute',
            zIndex: 10,
            display: 'flex',
            flexFlow: 'column nowrap',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
            boxSizing: 'border-box'
        }),
        darklight: {
            width: '100%',
            height: '100%',
            zIndex: 50,
            background: "hsla(0, 0%, 33%, 0.2)",
            position: 'absolute'
        },
        base: prefixCSS({
            minWidth: 40,
            width: '100%',
            flexBasis: '100%',
            flexShrink: 100
        }),
        absWrapper: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
        }
    }
};

const NavButton = ({media, onClick, children, label = '', carouselHeight}) => {
    const isMobile = useBreakpoint() === 'mobile';
    const [{hover}, ref] = useA11yButton({
        'aria-label': label
    }, !!media);

    const content = !media ? null :
        <div style={styles.navButton.content} onClick={onClick} role="button">
            <Image media={media} style={styles.navButton.image} />
            <div role="none" style={styles.navButton.children}>{children}</div>
            {hover && <div role="none" style={styles.navButton.darklight} />}
        </div>;

    return isMobile
    ?
          <div ref={ref} style={{...styles.navButton.base, height: 0, paddingTop: '100%', position: 'relative', maxWidth: 0.75 * carouselHeight}}>
              <div style={styles.navButton.absWrapper}>
                  {content}
              </div>
          </div>
    :
        <div ref={ref} style={{...styles.navButton.base, maxWidth: '100%', height: !media ? 0 : carouselHeight}}>
            {content}
        </div>;
};

const CarouselEvent = ({event: e, isEditor, isMobile, carouselHeight}) => {
    let children = null;

    const dateFontSize = useSelector(s => s.measurements.event_carousel_date_font_size);
    const monthFontSize = useSelector(s => s.measurements.event_carousel_month_font_size);
    const titleFontSize = useSelector(s => s.measurements.event_carousel_title_font_size);

    if (e && e.meta) {
        const d = parseDate(e.meta.event_start_timestamp);
        children = <>
            <div key="timeinfo" role="none">
                <HighlightedText style={{fontSize: dateFontSize}}>{dateDayZeroPadded(d)}</HighlightedText>
                <HighlightedText style={{fontSize: monthFontSize}}>{dateMonthShort(d)}</HighlightedText>
            </div>
            <div key="titleinfo" role="none">
                <HighlightedText style={{fontSize: titleFontSize}}>{e.meta.tile_title ? e.meta.tile_title : e.title}</HighlightedText>
            </div>
        </>;
    }
    /* eslint-disable */
    const media = e && ((e.meta && e.meta.tile_background) || (e.featured_media && e.featured_media.default));
    /* eslint-enable */

    const desktopStyles = {paddingLeft: 5, paddingRight: 5, flexBasis: '25%'};
    const mobileStyles = {width: '100%', marginBottom: 20};

    return (
        <div style={{...isMobile ? mobileStyles : desktopStyles, boxSizing: 'border-box', flexGrow: 2, height: carouselHeight}}>
            <HoverImageTile hoverEnabled={!!e} href={e && e.permalink} style={{height: '100%'}} clickable={!isEditor && !!e} media={media}>
                {children}
            </HoverImageTile>
        </div>
    );
};

export const EventsCarousel = ({perPage=3, carouselHeight=300, gutenberg:{isEditor}}) => {
    const isMobile = useBreakpoint() === 'mobile';
    const [offset, setOffset] = useState(0);
    const {loading, result} = usePromise(
        () => getWPJSON('/polyphony-events/v1/list', {offset, count: perPage}),
        [offset, perPage]
    );

    if (loading) {
        return <div style={prefixCSS({height: carouselHeight, display: 'flex', justifyContent: 'center', alignItems: 'center'})}>
            <Loader />
        </div>;
    }

    const {next, previous, events} = result;

    const prevMedia = previous
                       ? previous.meta.tile_background
                            ? previous.meta.tile_background
                            : previous.featured_media
                                  ? previous.featured_media.default
                                  : null
                       : null;
    const nextMedia = next
                       ? next.meta.tile_background
                            ? next.meta.tile_background
                            : next.featured_media
                                  ? next.featured_media.default
                                  : null
                       : null;

    const backJsx = <NavButton label='See older events' media={prevMedia} onClick={() => setOffset(offset - 1)} carouselHeight={carouselHeight}>
        <svg role="none" height={33} width={33} viewBox="0 0 320 512" role='none'>
            <path role="none" fill="currentColor" d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z" />
        </svg>
    </NavButton>;

    const nextJsx = <NavButton label='See newer events' media={nextMedia} onClick={() => setOffset(offset + 1)} carouselHeight={carouselHeight}>
        <svg role="none" height={33} width={33} viewBox="0 0 320 512">
            <path role="none" fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z" />
        </svg>
    </NavButton>;

    return (
        <div style={prefixCSS({display: "flex", justifyContent: 'center', alignItems: 'center', flexFlow: isMobile ? 'column' : 'row', height: isMobile ? "auto" : carouselHeight, margin: 5})}>
            {!isMobile && backJsx}
            {events.map((e, idx) => <CarouselEvent event={e} key={`${e.ID} ${idx}`} isEditor={isEditor} isMobile={isMobile} carouselHeight={carouselHeight} />)}
            {!isMobile && times(perPage - events.length, idx => <CarouselEvent key={`anti-${idx}`} isEditor={isEditor} isMobile={isMobile} carouselHeight={carouselHeight} />)}
            {!isMobile && nextJsx}
            {isMobile && <SeeMore />}
        </div>
    );
};

const SeeMore = () => {
    const [{hover}, ref] = useHover();

    let astyle = {
        color: hover ? 'lightgray' : undefined
    };

    return (
        <div role='none' style={prefixCSS({width: '100%', display: 'flex', flexFlow: 'row nowrap', alignItems: 'center', justifyContent: 'center'})}>
            <a ref={ref} style={astyle} href="/event">See all Events →</a>
        </div>
    );
};

EventsCarousel.slug = 'postcarousel';
EventsCarousel.title = "Events Carousel";
EventsCarousel.icon = 'wp';
EventsCarousel.category = 'layout';

